<template>
  <div class="modal-overlay">
    <transition name="modal">
      <div class="modal-container" @click.stop>

        <!-- Header -->
        <div class="modal-header">
          <ion-icon name="alert-circle-outline" class="modal-icon" />
          <h2 class="modal-title">Confirmar Eliminación</h2>
          <div class="header-divider"></div>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <template v-if="!successMessage">
            <p v-if="user" class="confirm-text">
              ¿Estás seguro de que deseas eliminar al usuario <strong>{{ user.username }}</strong>?
            </p>
            <p class="warning-msg">
              Esta acción no se puede deshacer y los datos del usuario serán eliminados permanentemente.
            </p>
          </template>

          <template v-else>
            <div class="success-msg">Usuario eliminado con éxito</div>
          </template>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <template v-if="!loading && !successMessage">
            <button class="cancel-btn" @click="closeModal">Cancelar</button>
            <button class="delete-btn" @click="handleDelete">Eliminar</button>
          </template>

          <template v-else-if="loading">
            <div class="loading-msg">
              <span class="spinner"></span>
              Eliminando usuario...
            </div>
          </template>
        </div>

      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "DeleteUserModal",

  props: {
    user: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      successMessage: "", // Estado del mensaje de éxito
    };
  },

  methods: {
    closeModal() {
      this.$emit("close");
    },

    async handleDelete() {
      if (!this.user?.id) return;

      this.loading = true;
      this.successMessage = "";

      try {
        // Llamamos al padre para que borre el usuario
        await this.$emit("confirm-delete");

        // Mostramos mensaje de éxito antes de cerrar
        this.successMessage = "Usuario eliminado con éxito";

        setTimeout(() => {
          this.closeModal();
        }, 1200);
      } catch (err) {
        console.error("Error al eliminar:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ========== Animación modal ========== */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.modal-enter, .modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* ========== Overlay ========== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  font-family: 'Manrope', sans-serif;
}

/* ========== Contenedor ========== */
.modal-container {
  background: #ffffff;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  animation: fadeIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ========== Header ========== */
.modal-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.modal-icon {
  font-size: 1.4rem;
  color: #dc2626;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  display: flex;
  align-items: center;
  flex: 1;
}

.header-divider {
  flex: 1;
  height: 1.3px;
  background: #e2e8f0;
}

/* ========== Cuerpo ========== */
.modal-body {
  font-size: 0.95rem;
  color: #334155;
  text-align: center;
  line-height: 1.6;
}

.modal-body strong {
  color: #0f172a;
}

.warning-msg {
  margin-top: 0.75rem;
  font-weight: 600;
  color: #dc2626;
}

/* ========== Footer ========== */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
  gap: 0.75rem;
}

.cancel-btn {
  padding: 0.55rem 1rem;
  background-color: #f8fafc;
  color: #334155;
  border: 1.5px solid #cbd5e1;
  border-radius: 0.65rem;
  font-size: 0.92rem;
  font-weight: 500;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background-color: #e2e8f0;
  border-color: #94a3b8;
  color: #1e293b;
}

.delete-btn {
  padding: 0.55rem 1.3rem;
  background-color: #dc2626;
  color: #ffffff;
  border: none;
  border-radius: 0.65rem;
  font-size: 0.92rem;
  font-weight: 500;
  transition: all 0.25s ease;
}

.delete-btn:hover {
  background-color: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(220, 38, 38, 0.2);
}

/* ========== Estado de carga ========== */
.loading-msg {
  font-size: 0.9rem;
  font-weight: 600;
  color: #334155;
  background-color: #f1f5f9;
  padding: 0.55rem 1.3rem;
  border-radius: 0.65rem;
  border: 1px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #cbd5e1;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========== Éxito ========== */
.success-msg {
  font-size: 0.9rem;
  font-weight: 600;
  color: #059669;
  background-color: #ecfdf5;
  padding: 0.55rem 1.3rem;
  border-radius: 0.65rem;
  border: 1px solid #34d399;
  text-align: center;
  width: 90%;
}
</style>
